import React, { ReactNode } from 'react';
import { Flex } from 'glints-aries/es';
import { OutlineButton, Typography } from 'glints-aries/es/@next';
import { Neutral } from 'glints-aries/es/@next/utilities/colors';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';

import { EmployerPurchaseProductType } from '../../../../common/interfaces/entities';
import { ROUTES } from '../../../../common/routes';
import { UnverifiedCompanyProductUsageWarningBanner } from '../../SharedComponents/UnverifiedCompanyProductUsageWarningBanner';
import { CreditProductPurchaseOrder } from '../../types';
import {
  ProductIcon,
  StyledButtonGroup,
  SuccessModalIntro,
  SuccessModalLink,
  SuccessModalSummary,
} from './styles.sc';
import { TermsAgreementText } from './TermsAgreementModal';

const messages = defineMessages({
  chatsAdded: {
    id: 'text-chats-added',
    defaultMessage: 'Chats Added',
  },
  purchaseSuccessfully: {
    id: 'text-purchased-successfully',
    defaultMessage: 'Purchased successfully',
  },
  yourRTChat: {
    id: 'text-your-recommended-talent-chat',
    defaultMessage:
      'Your Recommended Talent chat balance will be valid till <bold>{date}.</bold>',
  },
  nowYouHaveChats: {
    id: 'text-now-you-have-chats',
    defaultMessage:
      'Now you have {count, plural, =1 {<bold>{count}</bold> chat} other {<bold>{count}</bold> chats}}. Check your <link>chat balance</link> for more details.',
  },
});

type Props = {
  creditProductPurchaseOrder: Pick<
    CreditProductPurchaseOrder,
    'companyProductInventory'
  >;
  onClose: () => void;
};
const ModalTitle = () => (
  <FormattedMessage {...messages.purchaseSuccessfully} />
);

const ModalFooter = ({ onClose }: { onClose: () => void }) => (
  <StyledButtonGroup>
    <OutlineButton onClick={onClose}>
      <Typography variant="button">
        <FormattedMessage id="interactive-continue" defaultMessage="Continue" />
      </Typography>
    </OutlineButton>
  </StyledButtonGroup>
);

const Bold = (...chunks: ReactNode[]) => (
  <Typography variant="caption" as="span">
    {chunks}
  </Typography>
);

function RecommendedTalentChatSuccessModalContentComponent({
  creditProductPurchaseOrder: { companyProductInventory },
  onClose,
}: Props) {
  const { formatDate, formatNumber } = useIntl();

  return (
    <Flex
      flexDirection="column"
      style={{
        gap: 20,
      }}
    >
      <SuccessModalIntro>
        <ProductIcon
          src="/images/credit-system/recommended-talent-icon.svg"
          alt="Recommended talent"
        />
        <SuccessModalSummary>
          <Typography variant="body2">
            <FormattedMessage {...messages.chatsAdded} />
          </Typography>
          <Typography variant="subtitle2" as="div" color={Neutral.B40}>
            <FormattedMessage
              {...messages.yourRTChat}
              values={{
                date: companyProductInventory?.expiryDateTime
                  ? formatDate(companyProductInventory.expiryDateTime, {
                      year: 'numeric',
                      month: 'short',
                      day: '2-digit',
                    })
                  : '-',
                bold: Bold,
              }}
            />
          </Typography>
          <Typography variant="subtitle2" as="div" color={Neutral.B40}>
            <FormattedMessage
              {...messages.nowYouHaveChats}
              values={{
                count: formatNumber(companyProductInventory?.amount || 0),
                bold: Bold,
                link: (chunks: React.ReactNode) => (
                  <SuccessModalLink
                    to={`/${ROUTES.features}?tab=PURCHASES&purchasedTab=${EmployerPurchaseProductType.RECOMMENDED_TALENT_CHAT}`}
                    onClick={onClose}
                  >
                    {chunks}
                  </SuccessModalLink>
                ),
              }}
            />
          </Typography>
        </SuccessModalSummary>
      </SuccessModalIntro>
      <UnverifiedCompanyProductUsageWarningBanner />
      <TermsAgreementText />
    </Flex>
  );
}

export const RecommendedTalentChatSuccessModalContent = Object.assign(
  RecommendedTalentChatSuccessModalContentComponent,
  {
    ModalTitle,
    ModalFooter,
  }
);
